import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, isRef as _isRef, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "flex items-center border-b px-3" }
const _hoisted_2 = { class: "flex items-center gap-1 shrink-0" }
const _hoisted_3 = {
  key: 0,
  class: "border-b p-3 space-y-3"
}
const _hoisted_4 = { class: "flex flex-wrap gap-1.5" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "flex flex-wrap gap-2" }
const _hoisted_7 = {
  key: 0,
  class: "py-6 text-center text-sm text-muted-foreground"
}
const _hoisted_8 = { class: "px-2 pb-2 grid grid-cols-3 gap-1.5" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "text-xs font-medium" }
const _hoisted_11 = { class: "flex-1 truncate" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "flex justify-end px-3 py-1.5" }
const _hoisted_14 = { class: "flex-1 truncate" }
const _hoisted_15 = {
  key: 0,
  class: "shrink-0 text-xs text-muted-foreground"
}
const _hoisted_16 = {
  key: 4,
  class: "py-6 text-center text-sm text-muted-foreground"
}
const _hoisted_17 = {
  key: 1,
  class: "flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground"
}
const _hoisted_18 = { key: 0 }

import { onMounted, onUnmounted, computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useGlobalSearch, type SearchEntityType } from '@/composables/useGlobalSearch'
import { useProjectsStore } from '@/views/Projects/store'
import { Dialog } from '@/components/ui/dialog'
import { Command, CommandList, CommandGroup, CommandItem, CommandSeparator } from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
} from 'reka-ui'
import {
  Search,
  X,
  SlidersHorizontal,
  Clock,
  ArrowRight,
  Loader2,
  FolderKanban,
  AppWindow,
  FileText,
  TicketCheck,
  CalendarDays,
  Building2,
} from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'GlobalSearch',
  setup(__props) {

const router = useRouter()
const inputRef = ref<HTMLInputElement>()

const {
  isOpen,
  searchQuery,
  debouncedQuery,
  isAdvancedMode,
  advancedFilters,
  recentSearches,
  isLoadingStores,
  searchGroups,
  totalResults,
  navigateToResult,
  selectRecentSearch,
  removeRecentSearch,
  clearRecentSearches,
  toggleEntityType,
  resetAdvancedFilters,
} = useGlobalSearch()

const ENTITY_TYPE_OPTIONS: { type: SearchEntityType; label: string; icon: typeof FolderKanban }[] = [
  { type: 'project', label: 'Proyectos', icon: FolderKanban },
  { type: 'application', label: 'Apps', icon: AppWindow },
  { type: 'requirement', label: 'Requerimientos', icon: FileText },
  { type: 'ticket', label: 'Tickets', icon: TicketCheck },
  { type: 'meeting', label: 'Reuniones', icon: CalendarDays },
  { type: 'organization', label: 'Organizaciones', icon: Building2 },
]

const projectsStore = useProjectsStore()

const projectOptions = computed(() =>
  projectsStore.projects.map((p) => ({ value: p.id, label: p.name })),
)

const showRecentSearches = computed(
  () => !debouncedQuery.value && recentSearches.value.length > 0,
)

const showEmpty = computed(
  () => debouncedQuery.value && searchGroups.value.length === 0 && !isLoadingStores.value,
)

const showIdleState = computed(
  () => !debouncedQuery.value && recentSearches.value.length === 0 && !isLoadingStores.value,
)

function isEntityTypeActive(type: SearchEntityType): boolean {
  const types = advancedFilters.value.entityTypes
  return types.length === 0 || types.includes(type)
}

function handleNavigateToList(routeName: string) {
  isOpen.value = false
  router.push({ name: routeName })
}

// Cmd+K / Ctrl+K global shortcut
function handleKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    isOpen.value = !isOpen.value
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// Focus input when dialog opens
function handleOpenChange(open: boolean) {
  isOpen.value = open
  if (open) {
    nextTick(() => {
      inputRef.value?.focus()
    })
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Button), {
      variant: "outline",
      class: "relative h-9 w-9 px-0 md:h-9 md:w-auto md:px-4 md:pr-12",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (isOpen.value = true))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Search), { class: "h-4 w-4 md:mr-2" }),
        _cache[8] || (_cache[8] = _createElementVNode("span", { class: "hidden md:inline-flex text-sm text-muted-foreground" }, "Buscar...", -1)),
        _cache[9] || (_cache[9] = _createElementVNode("kbd", { class: "pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 hidden md:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground" }, [
          _createElementVNode("span", { class: "text-xs" }, "⌘"),
          _createTextVNode("K ")
        ], -1))
      ]),
      _: 1
    }),
    _createVNode(_unref(Dialog), {
      open: _unref(isOpen),
      "onUpdate:open": handleOpenChange
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(DialogPortal), null, {
          default: _withCtx(() => [
            _createVNode(_unref(DialogOverlay), { class: "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" }),
            _createVNode(_unref(DialogContent), {
              class: "fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg border bg-background p-0 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
              onEscapeKeyDown: _cache[7] || (_cache[7] = ($event: any) => (isOpen.value = false))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DialogClose), { class: "sr-only" }, {
                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                    _createTextVNode("Cerrar", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_unref(Command), { class: "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                      _createVNode(_unref(Search), { class: "mr-2 h-4 w-4 shrink-0 opacity-50" }),
                      _withDirectives(_createElementVNode("input", {
                        ref_key: "inputRef",
                        ref: inputRef,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(searchQuery) ? (searchQuery).value = $event : null)),
                        class: "flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
                        placeholder: "Buscar proyectos, requerimientos, tickets..."
                      }, null, 512), [
                        [_vModelText, _unref(searchQuery)]
                      ]),
                      _createElementVNode("div", _hoisted_2, [
                        (_unref(searchQuery))
                          ? (_openBlock(), _createBlock(_unref(Button), {
                              key: 0,
                              variant: "ghost",
                              size: "icon",
                              class: "h-7 w-7",
                              onClick: _cache[2] || (_cache[2] = ($event: any) => (searchQuery.value = ''))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(X), { class: "h-3.5 w-3.5" })
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true),
                        _createVNode(_unref(Button), {
                          variant: "ghost",
                          size: "icon",
                          class: _normalizeClass(["h-7 w-7", { 'bg-accent': _unref(isAdvancedMode) }]),
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (isAdvancedMode.value = !_unref(isAdvancedMode)))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(SlidersHorizontal), { class: "h-3.5 w-3.5" })
                          ]),
                          _: 1
                        }, 8, ["class"])
                      ])
                    ]),
                    (_unref(isAdvancedMode))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                          _createElementVNode("div", _hoisted_4, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(ENTITY_TYPE_OPTIONS, (opt) => {
                              return _createElementVNode("button", {
                                key: opt.type,
                                class: _normalizeClass(["inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors", 
                  isEntityTypeActive(opt.type)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-transparent text-muted-foreground border-input hover:bg-accent'
                ]),
                                onClick: ($event: any) => (_unref(toggleEntityType)(opt.type))
                              }, [
                                (_openBlock(), _createBlock(_resolveDynamicComponent(opt.icon), { class: "h-3 w-3" })),
                                _createTextVNode(" " + _toDisplayString(opt.label), 1)
                              ], 10, _hoisted_5)
                            }), 64))
                          ]),
                          _createElementVNode("div", _hoisted_6, [
                            _createVNode(_unref(Select), {
                              "model-value": _unref(advancedFilters).projectId || '_all',
                              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_unref(advancedFilters).projectId = $event === '_all' ? '' : $event))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(SelectTrigger), { class: "h-8 w-[180px] text-xs" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(SelectValue), { placeholder: "Proyecto..." })
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_unref(SelectContent), null, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(SelectItem), { value: "_all" }, {
                                      default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                        _createTextVNode("Todos los proyectos", -1)
                                      ]))]),
                                      _: 1
                                    }),
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(projectOptions.value, (p) => {
                                      return (_openBlock(), _createBlock(_unref(SelectItem), {
                                        key: p.value,
                                        value: p.value
                                      }, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(p.label), 1)
                                        ]),
                                        _: 2
                                      }, 1032, ["value"]))
                                    }), 128))
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }, 8, ["model-value"]),
                            _withDirectives(_createElementVNode("input", {
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(advancedFilters).dateFrom) = $event)),
                              type: "date",
                              class: "h-8 rounded-md border border-input bg-transparent px-2 text-xs",
                              placeholder: "Desde"
                            }, null, 512), [
                              [_vModelText, _unref(advancedFilters).dateFrom]
                            ]),
                            _withDirectives(_createElementVNode("input", {
                              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(advancedFilters).dateTo) = $event)),
                              type: "date",
                              class: "h-8 rounded-md border border-input bg-transparent px-2 text-xs",
                              placeholder: "Hasta"
                            }, null, 512), [
                              [_vModelText, _unref(advancedFilters).dateTo]
                            ]),
                            _createVNode(_unref(Button), {
                              variant: "ghost",
                              size: "sm",
                              class: "h-8 text-xs",
                              onClick: _unref(resetAdvancedFilters)
                            }, {
                              default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                _createTextVNode(" Limpiar ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["onClick"])
                          ])
                        ]))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(CommandList), { class: "max-h-[400px]" }, {
                      default: _withCtx(() => [
                        (_unref(isLoadingStores))
                          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                              _createVNode(_unref(Loader2), { class: "mx-auto h-5 w-5 animate-spin mb-2" }),
                              _cache[13] || (_cache[13] = _createTextVNode(" Cargando datos... ", -1))
                            ]))
                          : (showIdleState.value)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _cache[14] || (_cache[14] = _createElementVNode("div", { class: "px-4 pt-4 pb-2" }, [
                                  _createElementVNode("p", { class: "text-xs font-medium text-muted-foreground uppercase tracking-wider" }, "Accesos rapidos")
                                ], -1)),
                                _createElementVNode("div", _hoisted_8, [
                                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(ENTITY_TYPE_OPTIONS, (opt) => {
                                    return _createElementVNode("button", {
                                      key: opt.type,
                                      class: "flex flex-col items-center gap-1.5 rounded-lg border border-transparent px-3 py-3 text-muted-foreground hover:bg-muted hover:text-foreground hover:border-border transition-colors",
                                      onClick: ($event: any) => {_unref(toggleEntityType)(opt.type); isAdvancedMode.value = true}
                                    }, [
                                      (_openBlock(), _createBlock(_resolveDynamicComponent(opt.icon), { class: "h-5 w-5" })),
                                      _createElementVNode("span", _hoisted_10, _toDisplayString(opt.label), 1)
                                    ], 8, _hoisted_9)
                                  }), 64))
                                ]),
                                _cache[15] || (_cache[15] = _createElementVNode("div", { class: "px-4 pt-2 pb-3 border-t" }, [
                                  _createElementVNode("p", { class: "text-xs text-muted-foreground" }, " Escribe para buscar en proyectos, requerimientos, tickets y mas. "),
                                  _createElementVNode("div", { class: "flex items-center gap-3 mt-2 text-[10px] text-muted-foreground/60" }, [
                                    _createElementVNode("span", null, [
                                      _createElementVNode("kbd", { class: "rounded border px-1 py-0.5 font-mono" }, "⌘K"),
                                      _createTextVNode(" Abrir busqueda")
                                    ]),
                                    _createElementVNode("span", null, [
                                      _createElementVNode("kbd", { class: "rounded border px-1 py-0.5 font-mono" }, "esc"),
                                      _createTextVNode(" Cerrar")
                                    ])
                                  ])
                                ], -1))
                              ], 64))
                            : (showRecentSearches.value)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                  _createVNode(_unref(CommandGroup), { heading: "Busquedas recientes" }, {
                                    default: _withCtx(() => [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(recentSearches), (term) => {
                                        return (_openBlock(), _createBlock(_unref(CommandItem), {
                                          key: term,
                                          value: term,
                                          class: "group",
                                          onSelect: ($event: any) => (_unref(selectRecentSearch)(term))
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_unref(Clock), { class: "h-4 w-4 shrink-0 text-muted-foreground" }),
                                            _createElementVNode("span", _hoisted_11, _toDisplayString(term), 1),
                                            _createElementVNode("button", {
                                              class: "ml-auto h-5 w-5 shrink-0 inline-flex items-center justify-center rounded-sm opacity-0 group-hover:opacity-100 hover:bg-accent",
                                              onClick: _withModifiers(($event: any) => (_unref(removeRecentSearch)(term)), ["stop"])
                                            }, [
                                              _createVNode(_unref(X), { class: "h-3 w-3" })
                                            ], 8, _hoisted_12)
                                          ]),
                                          _: 2
                                        }, 1032, ["value", "onSelect"]))
                                      }), 128))
                                    ]),
                                    _: 1
                                  }),
                                  _createElementVNode("div", _hoisted_13, [
                                    _createVNode(_unref(Button), {
                                      variant: "link",
                                      size: "sm",
                                      class: "h-auto p-0 text-xs",
                                      onClick: _unref(clearRecentSearches)
                                    }, {
                                      default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                        _createTextVNode(" Limpiar historial ", -1)
                                      ]))]),
                                      _: 1
                                    }, 8, ["onClick"])
                                  ])
                                ], 64))
                              : (_unref(searchGroups).length > 0)
                                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 3 }, _renderList(_unref(searchGroups), (group, groupIdx) => {
                                    return (_openBlock(), _createElementBlock(_Fragment, {
                                      key: group.entityType
                                    }, [
                                      (groupIdx > 0)
                                        ? (_openBlock(), _createBlock(_unref(CommandSeparator), { key: 0 }))
                                        : _createCommentVNode("", true),
                                      _createVNode(_unref(CommandGroup), {
                                        heading: group.label
                                      }, {
                                        default: _withCtx(() => [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.results, (result) => {
                                            return (_openBlock(), _createBlock(_unref(CommandItem), {
                                              key: result.id,
                                              value: `${result.entityType}-${result.id}`,
                                              onSelect: ($event: any) => (_unref(navigateToResult)(result))
                                            }, {
                                              default: _withCtx(() => [
                                                (_openBlock(), _createBlock(_resolveDynamicComponent(group.icon), { class: "h-4 w-4 shrink-0 text-muted-foreground" })),
                                                _createElementVNode("span", _hoisted_14, _toDisplayString(result.title), 1),
                                                (result.subtitle)
                                                  ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(result.subtitle), 1))
                                                  : _createCommentVNode("", true),
                                                (result.badge)
                                                  ? (_openBlock(), _createBlock(_unref(Badge), {
                                                      key: 1,
                                                      variant: result.badgeVariant,
                                                      class: "shrink-0 text-[10px]"
                                                    }, {
                                                      default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(result.badge), 1)
                                                      ]),
                                                      _: 2
                                                    }, 1032, ["variant"]))
                                                  : _createCommentVNode("", true)
                                              ]),
                                              _: 2
                                            }, 1032, ["value", "onSelect"]))
                                          }), 128)),
                                          (group.totalCount > group.results.length)
                                            ? (_openBlock(), _createBlock(_unref(CommandItem), {
                                                key: 0,
                                                value: `see-all-${group.entityType}`,
                                                class: "text-xs text-muted-foreground",
                                                onSelect: ($event: any) => (handleNavigateToList(group.listRouteName))
                                              }, {
                                                default: _withCtx(() => [
                                                  _createVNode(_unref(ArrowRight), { class: "h-3 w-3" }),
                                                  _createElementVNode("span", null, "Ver todos (" + _toDisplayString(group.totalCount) + ")", 1)
                                                ]),
                                                _: 2
                                              }, 1032, ["value", "onSelect"]))
                                            : _createCommentVNode("", true)
                                        ]),
                                        _: 2
                                      }, 1032, ["heading"])
                                    ], 64))
                                  }), 128))
                                : (showEmpty.value)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_16, " No se encontraron resultados para \"" + _toDisplayString(_unref(debouncedQuery)) + "\" ", 1))
                                  : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }),
                    (_unref(debouncedQuery) || showRecentSearches.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                          _cache[17] || (_cache[17] = _createElementVNode("div", { class: "flex items-center gap-3" }, [
                            _createElementVNode("span", null, [
                              _createElementVNode("kbd", { class: "rounded border px-1 py-0.5 font-mono text-[10px]" }, "↑↓"),
                              _createTextVNode(" Navegar")
                            ]),
                            _createElementVNode("span", null, [
                              _createElementVNode("kbd", { class: "rounded border px-1 py-0.5 font-mono text-[10px]" }, "↵"),
                              _createTextVNode(" Seleccionar")
                            ]),
                            _createElementVNode("span", null, [
                              _createElementVNode("kbd", { class: "rounded border px-1 py-0.5 font-mono text-[10px]" }, "esc"),
                              _createTextVNode(" Cerrar")
                            ])
                          ], -1)),
                          (_unref(debouncedQuery))
                            ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(_unref(totalResults)) + " resultado" + _toDisplayString(_unref(totalResults) !== 1 ? 's' : ''), 1))
                            : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})